import type { ChangeEvent, FC } from 'react';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { Button, Modal, Rating, Text } from '../../../ui-components';
import { Field } from '../../field';
import { GetIcon } from '../../icons';
import { ResponsiveImage } from '../../responsive-image';
import { MAX_MEDIA_FILES_NUMBER } from '../const';
import { ReviewGuideLines } from './ReviewGuidelines';
import type { FieldConfig } from '../../field';
import type { Photo, Video } from '../types';

export interface WriteReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenVideoModal: () => void;
    onProceedToUserData: () => void;
    removeMedia: (mediaToRemove: Photo | Video) => void;
    onPhotoSelect: (e: ChangeEvent<HTMLInputElement>) => void;
    media: (Photo | Video)[];
    form: UseFormReturn<
        {
            review: string;
            reviewTitle: string;
            rating: number;
            firstName: string;
            lastName: string;
            email: string;
            nickName: string;
            video: string;
            photo: string;
        },
        unknown,
        undefined
    >;
    fields: Record<string, FieldConfig>;
    isFileSizeLimitExceeded?: boolean;
}
export const WriteReviewModal: FC<WriteReviewModalProps> = ({
    isOpen,
    form,
    onClose,
    fields,
    onProceedToUserData,
    onOpenVideoModal,
    onPhotoSelect,
    media,
    removeMedia,
    isFileSizeLimitExceeded = false,
}) => {
    const intl = useIntl();
    const { product } = usePDP();

    const buttonDirection = useBreakpointValue({ base: 'vertical', sm: 'horizontal' });

    if (!product) {
        return null;
    }

    const onRatingChanged = (rating: number): void => {
        form.clearErrors('rating');
        form.setValue('rating', rating);
    };

    return (
        <Modal
            id="review-modal"
            title={intl.formatMessage({
                id: 'turn-to.review-modal.share-experience',
                defaultMessage: 'Please share your experience',
            })}
            isOpen={isOpen}
            onClose={onClose}
            variant="turn-to"
        >
            <Flex gap={4}>
                <Box flex={1}>
                    <Text style="bold" size="small" margin="0 0 4px 0">
                        {product?.name}
                    </Text>
                    <Text margin="0 0 8px 0">
                        <FormattedMessage
                            id="turn-to.review-modal.feedback"
                            defaultMessage="Your feedback will help other shoppers make good choices, and we'll use it to improve our products."
                        />
                    </Text>
                    <ReviewGuideLines />
                </Box>
                <Box w="160px" h="160px">
                    {product?.imageGroups?.[0].images[0].link ? (
                        <ResponsiveImage
                            id={`turn-to-review-modal-image-${product?.id}`}
                            src={product?.imageGroups?.[0].images[0].link}
                            alt={product?.name}
                            width="160px"
                            height="160px"
                        />
                    ) : null}
                </Box>
            </Flex>
            <Box marginY={4}>
                <Text margin="0 0 4px 0">
                    <FormattedMessage
                        id="turn-to.review-modal.overall-rating"
                        defaultMessage="Overall rating"
                    />
                </Text>
                <Rating
                    initialRating={form.getValues('rating')}
                    iconName="rounded-star"
                    emptyIconName="empty-rounded-star"
                    iconSize="lg"
                    onRatingChanged={onRatingChanged}
                />
                <Field {...fields.rating} />
            </Box>
            <Flex gap={4} flexWrap="wrap" marginBottom={4}>
                <Field {...fields.review} variant="new-style" />
                <Field {...fields.reviewTitle} variant="new-style" />
            </Flex>
            <Text style="bold">
                <FormattedMessage
                    id="turn-to.review-modal.photos-or-videos"
                    defaultMessage="Photos or videos"
                />
            </Text>
            <Flex gap={2} flexWrap="wrap" rowGap={1} marginTop={1} marginBottom={2}>
                {media.map((mediaFile) => (
                    <Flex
                        width={{ base: '100%', md: 'calc(50% - 0.5rem)' }}
                        key={mediaFile.id}
                        background="slt-grey.100"
                        padding={2}
                    >
                        {mediaFile.type === 'photo' && (
                            <Box width="46px" height="46px">
                                <img
                                    src={mediaFile.b64data}
                                    alt={mediaFile.caption || ''}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </Box>
                        )}

                        {mediaFile.type === 'video' && (
                            <Box width="46px" height="46px">
                                <img
                                    width="100%"
                                    height="100%"
                                    src={mediaFile.thumbnailUrl}
                                    alt={mediaFile.caption || ''}
                                />
                            </Box>
                        )}

                        <Box flex={1}>
                            <Text margin="8px 4px" size="small" color="slt-pepper.700">
                                {mediaFile.caption || (
                                    <FormattedMessage
                                        id="turn-to.review-modal.no-caption"
                                        defaultMessage="no caption"
                                    />
                                )}
                            </Text>
                        </Box>

                        <Box
                            onClick={() => removeMedia(mediaFile)}
                            data-testid={`close-icon-${mediaFile.id}`}
                        >
                            <GetIcon name="close" size="sm" />
                        </Box>
                    </Flex>
                ))}
            </Flex>
            <Flex
                flexWrap={{ base: 'wrap', sm: 'nowrap' }}
                alignItems="center"
                className={buttonDirection === 'vertical' ? 'space-y-1' : 'space-x-1'}
                marginBottom={2}
            >
                <Button
                    isDisabled={media.length >= MAX_MEDIA_FILES_NUMBER}
                    variant="secondary"
                    leftIcon={<GetIcon name="photo" size="sm" />}
                    textMargin="0 0 0 16px"
                    wide
                >
                    <Field
                        {...fields.photo}
                        inputProps={{ onChange: onPhotoSelect }}
                        variant="new-style"
                    />
                </Button>
                <Button
                    isDisabled={media.length >= MAX_MEDIA_FILES_NUMBER}
                    variant="secondary"
                    leftIcon={<GetIcon name="video" size="sm" />}
                    onClick={onOpenVideoModal}
                    wide
                >
                    <FormattedMessage
                        id="turn-to.review-modal.add-video"
                        defaultMessage="Add video"
                    />
                </Button>
            </Flex>
            {form.formState.errors.photo ? (
                <Flex alignItems="center" gap={1}>
                    <Text size="small" color="slt-red.1000">
                        <FormattedMessage
                            id="turn-to.review-modal.media-limit"
                            defaultMessage="You may add up to five photos or videos"
                        />
                    </Text>
                </Flex>
            ) : (
                <Flex gap={1} direction="column">
                    <Text size="small">
                        <FormattedMessage
                            id="turn-to.review-modal.media-limit"
                            defaultMessage="You may add up to five photos or videos"
                        />
                    </Text>
                    <Text size="small">
                        <FormattedMessage
                            id="turn-to.review-modal.max-file-size"
                            defaultMessage="Max file size: 10MB"
                        />
                    </Text>
                </Flex>
            )}

            {isFileSizeLimitExceeded && (
                <Text color="slt-red-error">
                    <FormattedMessage
                        id="turn-to.review-modal.files-limit-exceeded"
                        defaultMessage="Your image is too large. Please upload an image under 10MB to share it with your review."
                    />
                </Text>
            )}

            <Box marginTop={8} marginBottom={{ base: 4, md: 0 }}>
                <Button variant="primary-black" onClick={onProceedToUserData} wide>
                    <FormattedMessage id="turn-to.review-modal.submit" defaultMessage="Submit" />
                </Button>
            </Box>
        </Modal>
    );
};
